// Generated from Swagger on 2025-08-27T14:46:24.223Z
import {
  useSuspenseQuery,
  useQuery,
  useMutation,
  useQueryClient,
  UseSuspenseQueryOptions,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { useContext } from "react";
import { ApiRequestOptions, useApiClient } from "../client";
import { creationEntitiesKeys } from "../creation-entities-keys";
import { formatSearchQuery } from "../../utils/format-search-query";
import { Context } from "@/util-components/root/context";
import {
  ApplyManagedImageEditRequestDto,
  ManagedImageResponseDto,
} from "../interfaces/media.interface";
import {
  TeamListPaginatedResponseDto,
  ApplyTeamLogoEditRequestDto,
  CreateTeamRequestDto,
  TeamResponseDto,
  TeamDetailsResponseDto,
  UpdateTeamRequestDto,
  TeamsUploadLogoFormData,
} from "../interfaces/teams.interface";

export interface UseTeamsFindParams {
  /**
   * The text to search for
   * @constraints minLength: 2, example: Jobdescription
   */
  textSearch?: string;
  /**
   * The maximum amount of retrieved items per page
   * @constraints minimum: 1, maximum: 20, default: 20, example: 20
   */
  limit?: number;
  /**
   * The page number to retrieve items from.
   * @constraints minimum: 1, default: 1, example: 1
   */
  page?: number;
}

// Query keys for teams-related queries
export const teamsKeys = {
  all: ["teams"] as const,
  profile: () => [...teamsKeys.all, "profile"] as const,
  mutation: () => [...teamsKeys.all, "mutation"] as const,
  details: () => [...teamsKeys.all, "detail"] as const,
  detail: (id: string) => [...teamsKeys.details(), id] as const,
  detail_mutation: (params?: any) =>
    [...teamsKeys.all, "detail_mutation", params] as const,
};

function invalidateTeamsQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: teamsKeys.all });
  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey as string[];
      return queryKey.includes("creation-entities");
    },
  });
}

/**
 * Find all teams with filter
 */
export function useTeamsFind(
  params?: UseTeamsFindParams,
  options?: Omit<
    UseSuspenseQueryOptions<TeamListPaginatedResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [...teamsKeys.profile(), ...creationEntitiesKeys.list(), params],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<TeamListPaginatedResponseDto>(`/teams${queryString}`);
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useTeamsFind
 */
export function useTeamsFindQuery(
  params?: UseTeamsFindParams,
  options?: Omit<
    UseQueryOptions<TeamListPaginatedResponseDto>,
    "queryKey" | "queryFn"
  > & { skipGlobalErrorHandler?: boolean },
) {
  const { apiRequest } = useApiClient();
  const { skipGlobalErrorHandler, ...queryOptions } = options || {};

  return useQuery({
    queryKey: [...teamsKeys.profile(), ...creationEntitiesKeys.list(), params],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<TeamListPaginatedResponseDto>(
        `/teams${queryString}`,
        "GET",
        undefined,
        undefined,
        { skipGlobalErrorHandler }
      );
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
    ...queryOptions,
  });
}

/**
 * Create a new team
 */
export function useTeamsCreate(
  options?: Omit<
    UseMutationOptions<TeamResponseDto, Error, CreateTeamRequestDto>,
    "mutationFn"
  >,
  requestOptions?: ApiRequestOptions,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();
  const { refetchUser } = useContext(Context);

  return useMutation({
    ...options,
    mutationFn: (data: CreateTeamRequestDto) =>
      apiRequest<TeamResponseDto>(`/teams`, "POST", data, undefined, requestOptions),
    onSuccess: (data, variables, context) => {
      invalidateTeamsQueries(queryClient);
      void refetchUser?.();
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Find one team
 */
export function useTeamsFindOne(
  id: string,
  options?: Omit<
    UseSuspenseQueryOptions<TeamDetailsResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [...teamsKeys.detail(id), ...creationEntitiesKeys.list()],
    queryFn: () => apiRequest<TeamDetailsResponseDto>(`/teams/${id}`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useTeamsFindOne
 */
export function useTeamsFindOneQuery(
  id: string,
  options?: Omit<
    UseQueryOptions<TeamDetailsResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [...teamsKeys.detail(id), ...creationEntitiesKeys.list()],
    queryFn: () => apiRequest<TeamDetailsResponseDto>(`/teams/${id}`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Update one team
 */
export function useTeamsUpdate(
  options?: Omit<
    UseMutationOptions<
      TeamDetailsResponseDto,
      Error,
      { id: string; data: UpdateTeamRequestDto }
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id, data }: { id: string; data: UpdateTeamRequestDto }) =>
      apiRequest<TeamDetailsResponseDto>(`/teams/${id}`, "PATCH", data),
    onSuccess: (data, variables, context) => {
      invalidateTeamsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete one team
 */
export function useTeamsDelete(
  options?: Omit<UseMutationOptions<any, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<any>(`/teams/${id}`, "DELETE"),
    onSuccess: (data, variables, context) => {
      invalidateTeamsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete multiple teams
 */
export function useTeamsBulkDelete(
  options?: Omit<UseMutationOptions<void, Error, { ids: string[] }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ ids }: { ids: string[] }) =>
      apiRequest<void>(`/teams/bulk-delete`, "POST", { ids }),
    onSuccess: (data, variables, context) => {
      invalidateTeamsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Upload team logo
 */
export function useTeamsUploadLogo(
  options?: Omit<
    UseMutationOptions<
      ManagedImageResponseDto,
      Error,
      { id: string; data: TeamsUploadLogoFormData }
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: TeamsUploadLogoFormData;
    }) => {
      const formData = new FormData();
      if (data.file) {
        formData.append("file", data.file);
      }
      return apiRequest<ManagedImageResponseDto>(`/teams/${id}/logo`, "POST", formData);
    },
    onSuccess: (data, variables, context) => {
      invalidateTeamsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete team logo
 */
export function useTeamsDeleteLogo(
  options?: Omit<UseMutationOptions<ManagedImageResponseDto, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<ManagedImageResponseDto>(`/teams/${id}/logo`, "DELETE"),
    onSuccess: (data, variables, context) => {
      invalidateTeamsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useTeamsApplyLogoEdit(
  options?: Omit<
    UseMutationOptions<ManagedImageResponseDto, Error, { id: string; data: ApplyTeamLogoEditRequestDto }>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id, data }: { id: string; data: ApplyManagedImageEditRequestDto }) =>
      apiRequest<ManagedImageResponseDto>(`/teams/${id}/logo/edit`, "POST", data),
    onSuccess: (data, variables, context) => {
      invalidateTeamsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useTeamsRestoreOriginalLogo(
  options?: Omit<UseMutationOptions<ManagedImageResponseDto, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<ManagedImageResponseDto>(`/teams/${id}/logo/restore-original`, "POST"),
    onSuccess: (data, variables, context) => {
      invalidateTeamsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useTeamsPreviewBackgroundRemoval(
  options?: Omit<UseMutationOptions<Blob, Error, { file: File }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();

  return useMutation({
    ...options,
    mutationFn: ({ file }: { file: File }) => {
      const formData = new FormData();
      formData.append("file", file);

      return apiRequest<Blob>(
        `/teams/media/preview-background-removal`,
        "POST",
        formData,
        undefined,
        { responseType: "blob", throwOnError: true }
      );
    },
  });
}
